import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { Box, useTheme } from '@mui/material';
import { tokens } from '../../../global/theme/tokens';
import { QuillDeltaToHtmlConverter } from 'quill-delta-to-html';
import { htmlToQuillDelta } from './quillHtmlToDelta.ts';
import { convertQuillHtmlToInlineAndClassHtml } from './convertQuillHtmlToInlineAndClassHtml.js';
import RenderHTML from '../../preview/RenderHTML.jsx';

export const fontSizes = ['6px', '8px', '12px', '15px', '18px', '24px'];
export const fontFamilies = ['montserrat'];

// FONT: use class-based attribution
const Font = Quill.import('attributors/class/font');
Font.whitelist = fontFamilies;
Quill.register(Font, true);

// SIZE: use class-based attribution (not style!)
const Size = Quill.import('attributors/class/size');
Size.whitelist = fontSizes;
Quill.register(Size, true);

const Editor = forwardRef(
  (
    {
      readOnly,
      defaultValue,
      onTextChange,
      onSelectionChange,
      onHtmlChange,
      hasError,
      afterHtml,
      afterComponent,
      defaultColor,
      bgcolor,
    },
    ref
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

    const containerRef = useRef(null);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const onHtmlChangeRef = useRef(onHtmlChange);

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
      onHtmlChangeRef.current = onHtmlChange;
    });

    useEffect(() => {
      ref.current?.enable(!readOnly);
    }, [ref, readOnly]);

    useEffect(() => {
      const container = containerRef.current;
      const editorContainer = container.appendChild(
        container.ownerDocument.createElement('div')
      );
      const quill = new Quill(editorContainer, {
        theme: 'snow',
        modules: {
          toolbar: {
            container: '#custom-toolbar', // ID of your MUI toolbar container
          },
        },
      });

      ref.current = quill;

      if (defaultValue) {
        quill.setContents(htmlToQuillDelta(defaultValue));
      }

      quill.on(Quill.events.TEXT_CHANGE, (...args) => {
        const quillHtml = quill.root.innerHTML;
        const plainText = quill.getText().trim();
        const inlineHtml = convertQuillHtmlToInlineAndClassHtml(quillHtml);

        onTextChangeRef.current?.(...args);
        onHtmlChangeRef.current?.(inlineHtml, plainText);
      });

      quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
        onSelectionChangeRef.current?.(...args);
      });

      // DEFAULT STYLES
      quill.clipboard.addMatcher(Node.ELEMENT_NODE, function (node, delta) {
        delta.ops.forEach((op) => {
          if (!op.attributes) op.attributes = {};
          op.attributes.font = 'montserrat';
          op.attributes.color = defaultColor;
          op.attributes.size = '12px';
          op.attributes.code = false;
          op.attributes.background = undefined;
          op.attributes['code-block'] = undefined;
        });
        return delta;
      });

      quill.on('editor-change', (eventName, ...args) => {
        if (eventName === 'selection-change') {
          const range = args[0];
          if (range && range.length === 0) {
            const format = quill.getFormat(range);
            if (!format.font) quill.format('font', 'montserrat');
            if (!format.color) quill.format('color', defaultColor);
            if (!format.size) quill.format('size', '12px');
          }
        }
      });

      quill.format('font', 'montserrat');
      quill.format('color', defaultColor);

      return () => {
        ref.current = null;
        container.innerHTML = '';
      };
    }, [ref, defaultValue]);

    return (
      <Box
        sx={{
          borderLeft: `4px solid ${hasError ? colors.warning : colors.success}`,
          borderRadius: '12px',
          backgroundColor: bgcolor || colors.bgInput,
          padding: 2,
          '.ql-container': {
            border: '0px solid transparent',
          },
          '.ql-editor': {
            fontSize: '1rem',
            lineHeight: 1.5,
            padding: 0,
            minHeight: '200px',
          },
          '.ql-font-montserrat': {
            fontFamily: 'montserrat',
          },
          '*': {
            color: defaultColor || 'unset',
          },
        }}
      >
        <div ref={containerRef}></div> <Box className="pt-4" />
        {afterHtml && <RenderHTML HTML={afterHtml} />}
        {afterComponent}
      </Box>
    );
  }
);

Editor.displayName = 'Editor';

export default Editor;
